import { PlanTransition, PriceSummary } from "../components/UpgradeConfirmParts";

export function UpgradeConfirmationModal({
    isOpen, onClose, onConfirm, selectedPlan, currentPlanDetails,
    isYearly, isUpgrade, isDowngrade, isBillingCycleChange, isLoading, t,
}) {
    if (!isOpen || !selectedPlan) return null;

    const newPrice = isYearly ? selectedPlan.priceYearly : selectedPlan.price;
    // For billing cycle changes, the current plan is on the opposite cycle
    const isCurrentYearly = isBillingCycleChange ? !isYearly : isYearly;
    const currentPrice = currentPlanDetails
        ? (isCurrentYearly ? currentPlanDetails.priceYearly : currentPlanDetails.price)
        : "0";
    const priceDiff = (parseFloat(newPrice) - parseFloat(currentPrice)).toFixed(2);
    const isIncrease = parseFloat(priceDiff) > 0;

    const getPlanName = (plan) => {
        return plan?.nameKey
            ? t(`billing.planNames.${plan.nameKey}`, { defaultValue: plan.name })
            : plan?.name;
    };

    const getTitle = () => {
        if (isBillingCycleChange) return t("billing.upgradeConfirm.switchBillingTitle") || "Switch Billing Cycle";
        if (isDowngrade) return t("billing.upgradeConfirm.downgradeTitle") || "Confirm Downgrade";
        return t("billing.upgradeConfirm.upgradeTitle") || "Confirm Upgrade";
    };

    const getDescription = () => {
        if (isBillingCycleChange) {
            return isYearly
                ? (t("billing.upgradeConfirm.switchToYearlyDesc") || "You are switching to yearly billing. This will take effect on your next billing date.")
                : (t("billing.upgradeConfirm.switchToMonthlyDesc") || "You are switching to monthly billing. This will take effect on your next billing date.");
        }
        if (isDowngrade) {
            return t("billing.upgradeConfirm.downgradeDesc") || "Your plan will be downgraded at the end of your current billing period.";
        }
        return t("billing.upgradeConfirm.upgradeDesc") || "Your plan will be upgraded immediately and you will be charged the prorated difference.";
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-bold text-white">{getTitle()}</h2>
                        <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10" aria-label={t("common.close") || "Close"}>
                            x
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    <p className="text-sm text-gray-600">{getDescription()}</p>

                    <PlanTransition currentPlan={currentPlanDetails} newPlan={selectedPlan} isYearly={isYearly} getPlanName={getPlanName} t={t} />

                    <PriceSummary currentPrice={currentPrice} newPrice={newPrice} priceDiff={priceDiff} isIncrease={isIncrease} isYearly={isYearly} isDowngrade={isDowngrade && !isBillingCycleChange} t={t} />

                    <div className="flex gap-3 pt-2">
                        <button onClick={onClose} disabled={isLoading} className="flex-1 py-3 px-4 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
                            {t("common.cancel") || "Cancel"}
                        </button>
                        <button onClick={onConfirm} disabled={isLoading} className={`flex-1 py-3 px-4 rounded-xl font-semibold text-white transition-all disabled:opacity-50 ${isDowngrade && !isBillingCycleChange ? "bg-orange-500 hover:bg-orange-600" : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"}`}>
                            {isLoading ? (t("billing.processing") || "Processing...") : (t("billing.upgradeConfirm.confirm") || "Confirm")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
